import {
  currencyDigits,
  fromQty,
  type MileageUnit,
  UNITS,
  type UnitCode,
  utcToday,
} from "@bt/domain";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Intl locale for the UI language: English follows the browser's region (e.g. en-GB), German is
 * de-DE, and any other language is its own code ("fr", "de-AT").
 */
export function localeFor(language: string): string {
  if (language === "de") return "de-DE";
  if (language === "en") return navigator.languages.find((l) => l.startsWith("en")) ?? "en-GB";
  return language;
}

/** Parses "YYYY-MM-DD" as a local date (no UTC shift) or an ISO timestamp. */
export function parseDate(value: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(value);
}

/**
 * The today every comparison is made against ("is this rate current", "is this order overdue",
 * "in 3 days"): the UTC date, the same as the server's (architecture.md › UTC). Never the
 * browser's date, which runs ahead of the server's for hours after local midnight east of UTC.
 */
export function serverToday(): string {
  return utcToday(new Date());
}

/**
 * Today in the browser's time zone, "YYYY-MM-DD": only what date fields start with and the date
 * on a printout. Comparisons use `serverToday()`.
 */
export function localToday(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const DAY = 86_400_000;

export function useFormat() {
  const { t, i18n } = useTranslation();
  const locale = localeFor(i18n.language);

  return useMemo(() => {
    const numberFormats = new Map<number, Intl.NumberFormat>();
    const num = (value: number, maxDecimals = 2) => {
      let format = numberFormats.get(maxDecimals);
      if (!format) {
        format = new Intl.NumberFormat(locale, { maximumFractionDigits: maxDecimals });
        numberFormats.set(maxDecimals, format);
      }
      return format.format(value);
    };
    const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: "medium" });
    const shortDateFormat = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" });
    const monthFormat = new Intl.DateTimeFormat(locale, { month: "short" });
    const monthYearFormat = new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" });
    const dateTimeFormat = new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
    const relativeFormat = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

    const inputFormats = new Map<string, Intl.NumberFormat>();
    /**
     * Plain number for editing (no grouping), e.g. "1250,5"; `minDecimals` pads, so a price
     * reads "23,50".
     */
    const inputNumber = (value: number, maxDecimals: number, minDecimals = 0) => {
      const key = `${minDecimals}:${maxDecimals}`;
      let format = inputFormats.get(key);
      if (!format) {
        format = new Intl.NumberFormat(locale, {
          minimumFractionDigits: minDecimals,
          maximumFractionDigits: maxDecimals,
          useGrouping: false,
        });
        inputFormats.set(key, format);
      }
      return format.format(value);
    };

    const percentFormat = new Intl.NumberFormat(locale, {
      style: "percent",
      maximumFractionDigits: 2,
    });

    /** Amount without unit, e.g. "5,5". */
    const qtyNumber = (qty: number, unit: UnitCode) => num(fromQty(qty), UNITS[unit].decimals);

    return {
      locale,
      number: num,
      inputNumber,
      qtyNumber,
      /** Amount with unit, e.g. "5.5 L" or "3 pcs". */
      qty: (qty: number, unit: UnitCode) =>
        t(`units.${unit}`, { count: fromQty(qty), value: qtyNumber(qty, unit) }),
      /** Signed change, e.g. "+2 pcs" / "−1.5 L". */
      delta: (qty: number, unit: UnitCode) => {
        const sign = qty > 0 ? "+" : qty < 0 ? "−" : "";
        const abs = Math.abs(qty);
        return `${sign}${t(`units.${unit}`, { count: fromQty(abs), value: qtyNumber(abs, unit) })}`;
      },
      money: (amountMinor: number, currency: string) =>
        new Intl.NumberFormat(locale, { style: "currency", currency }).format(
          amountMinor / 10 ** currencyDigits(currency),
        ),
      /** "9,90–12,90 €", or one amount when both ends are equal. */
      moneyRange: (range: { minMinor: number; maxMinor: number; currency: string }) => {
        const money = new Intl.NumberFormat(locale, {
          style: "currency",
          currency: range.currency,
        });
        const digits = 10 ** currencyDigits(range.currency);
        return range.minMinor === range.maxMinor
          ? money.format(range.minMinor / digits)
          : money.formatRange(range.minMinor / digits, range.maxMinor / digits);
      },
      /** A tax rate in basis points, e.g. "19 %" or "8,1 %". */
      taxRate: (bp: number) => percentFormat.format(bp / 10_000),
      date: (value: string | null | undefined) =>
        value ? dateFormat.format(parseDate(value)) : "",
      shortDate: (value: string | null | undefined) =>
        value ? shortDateFormat.format(parseDate(value)) : "",
      monthShort: (value: string) => monthFormat.format(parseDate(value)).replace(".", ""),
      /** "Mar 2027" / "März 2027" for the month a calendar date is in. */
      monthYear: (value: string) => monthYearFormat.format(parseDate(value)),
      dateTime: (value: string | null | undefined) =>
        value ? dateTimeFormat.format(parseDate(value)) : "",
      /** "today", "in 3 days", "2 weeks ago" for calendar dates. */
      relativeDate: (value: string) => {
        const target = parseDate(value);
        const today = parseDate(serverToday());
        const days = Math.round((target.getTime() - today.getTime()) / DAY);
        if (Math.abs(days) < 14) return relativeFormat.format(days, "day");
        if (Math.abs(days) < 60) return relativeFormat.format(Math.round(days / 7), "week");
        return relativeFormat.format(Math.round(days / 30), "month");
      },
      /** "3 minutes ago" for timestamps. */
      relativeTime: (iso: string) => {
        const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
        const abs = Math.abs(seconds);
        if (abs < 60) return relativeFormat.format(Math.round(seconds), "second");
        if (abs < 3600) return relativeFormat.format(Math.round(seconds / 60), "minute");
        if (abs < 86_400) return relativeFormat.format(Math.round(seconds / 3600), "hour");
        if (abs < 86_400 * 14) return relativeFormat.format(Math.round(seconds / 86_400), "day");
        return dateFormat.format(new Date(iso));
      },
      mileage: (value: number | null | undefined, unit: MileageUnit | string) =>
        value === null || value === undefined ? "" : `${num(value, 0)} ${unit}`,
    };
  }, [locale, t]);
}

export type Formatters = ReturnType<typeof useFormat>;
